import React, { useState, useEffect } from 'react';
import {
    Stack, Text, DetailsList, DefaultButton, MessageBar, MessageBarType,
    Dialog, DialogType, DialogFooter, PrimaryButton, Spinner,
    Pivot, PivotItem, PivotLinkSize
} from '@fluentui/react';

function AdminPanel() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // User detail state
    const [selectedUser, setSelectedUser] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    // Dialogs
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteResult, setDeleteResult] = useState(null);

    // Force sync
    const [forceSyncDialogOpen, setForceSyncDialogOpen] = useState(false);
    const [userToSync, setUserToSync] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    const [message, setMessage] = useState(null);

    // Metadata backfill
    const [backfilling, setBackfilling] = useState(false);
    const [backfillReport, setBackfillReport] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const runBackfill = async (dryRun) => {
        setBackfilling(true);
        setBackfillReport(null);
        try {
            const response = await fetch('/api/mgmt-backfill-metadata', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dryRun: !!dryRun })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
            setBackfillReport(data);
            const s = data.summary || {};
            setMessage({
                type: MessageBarType.success,
                text: `${dryRun ? 'Dry-run ' : ''}backfill done — accounts: ${s.accountsUpdated}/${s.accountsScanned} updated (${s.accountsSkipped} skipped); calendars: ${s.calendarsUpdated}/${s.calendarsScanned} updated (${s.calendarsSkipped} skipped).`
            });
        } catch (err) {
            setMessage({ type: MessageBarType.error, text: 'Backfill failed: ' + err.message });
        } finally {
            setBackfilling(false);
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api/mgmt-users-list');
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setError('Failed to load users: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserDetail = async (userId) => {
        setDetailLoading(true);
        setDetailError(null);
        setSelectedUser(null);
        try {
            const response = await fetch(`/api/mgmt-user-detail?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            setSelectedUser(data);
        } catch (err) {
            setDetailError('Failed to load user detail: ' + err.message);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setSelectedUser(null);
        setDetailError(null);
    };

    // Delete user
    const openDeleteDialog = (user) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
        setDeleteResult(null);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        setDeleteResult(null);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        setDeleting(true);
        setDeleteResult(null);
        try {
            const response = await fetch('/api/mgmt-user-delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: userToDelete.userId })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Delete failed');
            setDeleteResult({ success: true, deleted: data.deleted });
            const b2c = data.deleted?.b2c;
            const b2cMsg = b2c?.status === 'deleted' ? ' (B2C identity removed)'
                : b2c?.status === 'not_found' ? ' (B2C identity was already gone)'
                : b2c?.status === 'skipped' ? ` (B2C deletion skipped: ${b2c.reason})`
                : b2c?.status === 'failed' ? ` (B2C deletion FAILED: ${b2c.reason})`
                : '';
            setMessage({
                type: b2c?.status === 'failed' ? MessageBarType.warning : MessageBarType.success,
                text: `User ${userToDelete.displayName || userToDelete.email || userToDelete.userId} deleted.${b2cMsg}`
            });
            setUsers(prev => prev.filter(u => u.userId !== userToDelete.userId));
            if (selectedUser?.userId === userToDelete.userId) closeDetail();
        } catch (err) {
            setDeleteResult({ success: false, error: err.message });
        } finally {
            setDeleting(false);
        }
    };

    // Force sync
    const openForceSyncDialog = (user) => {
        setUserToSync(user);
        setForceSyncDialogOpen(true);
        setSyncResult(null);
    };

    const closeForceSyncDialog = () => {
        setForceSyncDialogOpen(false);
        setUserToSync(null);
        setSyncResult(null);
    };

    const confirmForceSync = async () => {
        if (!userToSync) return;
        setSyncing(true);
        setSyncResult(null);
        try {
            const response = await fetch('/api/mgmt-user-force-sync', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: userToSync.userId })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || data.message || 'Sync failed');
            setSyncResult({ success: true, result: data });
            setMessage({ type: MessageBarType.success, text: `Sync triggered for ${userToSync.displayName || userToSync.email || userToSync.userId}.` });
        } catch (err) {
            setSyncResult({ success: false, error: err.message });
        } finally {
            setSyncing(false);
        }
    };

    const formatDate = (ts) => {
        if (!ts) return 'Never';
        return new Date(ts).toLocaleString();
    };

    const tokenStatusBadge = (status) => {
        const colors = {
            active: '#107C10',
            expired: '#D13438',
            pending: '#FFC107'
        };
        return (
            <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: colors[status] || '#605E5C',
                color: 'white',
                fontSize: '12px'
            }}>
                {status}
            </span>
        );
    };

    const userColumns = [
        {
            key: 'user',
            name: 'User',
            minWidth: 220,
            isResizable: true,
            onRender: (user) => {
                const hasAnyLabel = user.displayName || user.email;
                const primary = user.displayName || user.email || '(no profile)';
                const secondary = user.email && user.displayName && user.email !== user.displayName
                    ? user.email
                    : null;
                // When the label came from an Account row (B2C profile not
                // captured yet), we still want to flag that visually so the
                // admin knows it's a best-effort O365-tenant fallback.
                const labelIsFallback = hasAnyLabel && !user.identityProvider;
                return (
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                            {primary}
                            {labelIsFallback && (
                                <Text variant="xSmall" styles={{ root: { color: '#a19f9d', fontWeight: 400, marginLeft: 6 } }}>
                                    (from O365)
                                </Text>
                            )}
                        </Text>
                        {secondary && (
                            <Text variant="xSmall" styles={{ root: { color: '#605e5c' } }}>{secondary}</Text>
                        )}
                        <Text variant="xSmall" styles={{ root: { color: '#a19f9d', fontFamily: 'monospace' } }}>{user.userId}</Text>
                    </Stack>
                );
            }
        },
        {
            key: 'identityProvider',
            name: 'IdP',
            fieldName: 'identityProvider',
            minWidth: 90,
            maxWidth: 120,
            onRender: (user) => user.identityProvider || '—'
        },
        {
            key: 'accounts',
            name: 'Tenants',
            onRender: (user) => user.accounts?.map(a => a.tenantId).join(', ') || 'None',
            minWidth: 150
        },
        {
            key: 'tokenStatuses',
            name: 'Tokens',
            onRender: (user) => {
                const active = user.tokenStatuses?.filter(t => t.status === 'active').length || 0;
                const total = user.tokenStatuses?.length || 0;
                return `${active}/${total} active`;
            },
            minWidth: 80
        },
        {
            key: 'lastSync',
            name: 'Last Sync',
            onRender: (user) => formatDate(user.lastSync),
            minWidth: 150
        },
        {
            key: 'lastSyncStatus',
            name: 'Sync Status',
            onRender: (user) => {
                const s = user.lastSyncStatus;
                if (!s) return 'No runs';
                const color = s === 'completed' ? 'green' : s === 'failed' ? 'red' : 'orange';
                return <span style={{ color }}>{s}</span>;
            },
            minWidth: 100
        },
        {
            key: 'createdAt',
            name: 'Created',
            onRender: (user) => formatDate(user.createdAt),
            minWidth: 150
        },
        {
            key: 'actions',
            name: 'Actions',
            onRender: (user) => (
                <Stack horizontal tokens={{ childrenGap: 4 }}>
                    <DefaultButton
                        text="View"
                        onClick={() => fetchUserDetail(user.userId)}
                        styles={{ root: { minWidth: 60 } }}
                    />
                    <DefaultButton
                        text="Sync"
                        onClick={() => openForceSyncDialog(user)}
                        styles={{ root: { minWidth: 60 } }}
                    />
                    <DefaultButton
                        text="Delete"
                        onClick={() => openDeleteDialog(user)}
                        styles={{ root: { minWidth: 60, backgroundColor: '#d13438', color: 'white' } }}
                    />
                </Stack>
            ),
            minWidth: 220
        }
    ];

    if (loading) return <Spinner label="Loading users..." />;
    if (error) return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>;

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            {message && (
                <MessageBar
                    messageBarType={message.type}
                    onDismiss={() => setMessage(null)}
                >
                    {message.text}
                </MessageBar>
            )}

            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Text variant="xLarge">Admin Panel</Text>
                <DefaultButton text="Refresh" onClick={fetchUsers} />
            </Stack>

            <Stack styles={{ root: { border: '1px solid #edebe9', borderRadius: 6, padding: 16 } }} tokens={{ childrenGap: 10 }}>
                <Text variant="large">Metadata Backfill</Text>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                    Backfills <code>tenantName</code>, <code>userEmail</code>, <code>userDisplayName</code> on
                    accounts and <code>calendarName</code>, <code>ownerEmail</code>, <code>ownerDisplayName</code> on
                    calendars by replaying Microsoft Graph for any record where <code>metadataUpdatedAt</code> is missing.
                    Records whose token has expired (with no refresh) are skipped and reported below.
                </Text>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <DefaultButton
                        text={backfilling ? 'Running…' : 'Dry run'}
                        disabled={backfilling}
                        onClick={() => runBackfill(true)}
                    />
                    <PrimaryButton
                        text={backfilling ? 'Running…' : 'Run backfill'}
                        disabled={backfilling}
                        onClick={() => runBackfill(false)}
                    />
                </Stack>
                {backfillReport && (
                    <Stack tokens={{ childrenGap: 6 }} styles={{ root: { marginTop: 8 } }}>
                        <Text variant="medium">
                            Summary: accounts {backfillReport.summary?.accountsUpdated}/{backfillReport.summary?.accountsScanned} updated
                            ({backfillReport.summary?.accountsSkipped} skipped);
                            calendars {backfillReport.summary?.calendarsUpdated}/{backfillReport.summary?.calendarsScanned} updated
                            ({backfillReport.summary?.calendarsSkipped} skipped).
                        </Text>
                        {backfillReport.accounts?.length > 0 && (
                            <DetailsList
                                items={backfillReport.accounts}
                                selectionMode={0}
                                columns={[
                                    { key: 'tenantId', name: 'Tenant', fieldName: 'tenantId', minWidth: 200, maxWidth: 280, isResizable: true },
                                    { key: 'userId', name: 'User', fieldName: 'userId', minWidth: 180, maxWidth: 260, isResizable: true },
                                    { key: 'status', name: 'Status', fieldName: 'status', minWidth: 80, maxWidth: 100, isResizable: true },
                                    { key: 'detail', name: 'Detail', minWidth: 240, isResizable: true,
                                      onRender: (i) => i.reason || `${i.tenantName || ''} ${i.userEmail || ''}`.trim() || '—' }
                                ]}
                            />
                        )}
                        {backfillReport.calendars?.length > 0 && (
                            <DetailsList
                                items={backfillReport.calendars}
                                selectionMode={0}
                                columns={[
                                    { key: 'tenantId', name: 'Tenant', fieldName: 'tenantId', minWidth: 180, maxWidth: 240, isResizable: true },
                                    { key: 'mailboxId', name: 'Mailbox', fieldName: 'mailboxId', minWidth: 180, maxWidth: 320, isResizable: true },
                                    { key: 'status', name: 'Status', fieldName: 'status', minWidth: 80, maxWidth: 100, isResizable: true },
                                    { key: 'detail', name: 'Detail', minWidth: 240, isResizable: true,
                                      onRender: (i) => i.reason || `${i.calendarName || ''} (${i.ownerEmail || '?'})` }
                                ]}
                            />
                        )}
                    </Stack>
                )}
            </Stack>

            <Text variant="medium">Total users: {users.length}</Text>

            {users.length === 0 ? (
                <Text>No users found.</Text>
            ) : (
                <DetailsList
                    items={users}
                    columns={userColumns}
                    styles={{ root: { minWidth: 900 } }}
                />
            )}

            {/* User Detail Slide-in / Section */}
            {detailLoading && <Spinner label="Loading user detail..." />}
            {detailError && <MessageBar messageBarType={MessageBarType.error}>{detailError}</MessageBar>}

            {selectedUser && !detailLoading && (
                <Stack styles={{ root: { border: '1px solid #ddd', padding: 20, borderRadius: 8 } }}>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                        <Stack tokens={{ childrenGap: 2 }}>
                            <Text variant="large">
                                User Detail: {selectedUser.displayName || selectedUser.email || selectedUser.userId}
                            </Text>
                            {(selectedUser.email && selectedUser.email !== selectedUser.displayName) && (
                                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>{selectedUser.email}</Text>
                            )}
                            <Text variant="xSmall" styles={{ root: { color: '#a19f9d', fontFamily: 'monospace' } }}>
                                {selectedUser.userId}
                                {selectedUser.identityProvider ? ` · ${selectedUser.identityProvider}` : ''}
                            </Text>
                        </Stack>
                        <DefaultButton text="Close" onClick={closeDetail} />
                    </Stack>

                    <Pivot linkSize={PivotLinkSize.normal}>
                        <PivotItem headerText="Accounts">
                            <DetailsList
                                items={selectedUser.accounts || []}
                                columns={[
                                    { key: 'id', name: 'ID', fieldName: 'id', minWidth: 150 },
                                    { key: 'tenantId', name: 'Tenant', fieldName: 'tenantId', minWidth: 150 },
                                    { key: 'authType', name: 'Auth Type', fieldName: 'authType', minWidth: 120 },
                                    { key: 'createdAt', name: 'Created', onRender: (a) => formatDate(a.createdAt), minWidth: 150 }
                                ]}
                            />
                        </PivotItem>

                        <PivotItem headerText="Tokens">
                            <DetailsList
                                items={selectedUser.tokens || []}
                                columns={[
                                    { key: 'accountId', name: 'Account', fieldName: 'accountId', minWidth: 150 },
                                    { key: 'status', name: 'Status', onRender: (t) => tokenStatusBadge(t.status), minWidth: 100 },
                                    { key: 'expiresAt', name: 'Expires', onRender: (t) => formatDate(t.expires_at), minWidth: 150 },
                                    { key: 'updatedAt', name: 'Updated', onRender: (t) => formatDate(t.updatedAt), minWidth: 150 }
                                ]}
                            />
                        </PivotItem>

                        <PivotItem headerText="Calendars">
                            <DetailsList
                                items={selectedUser.calendars || []}
                                columns={[
                                    { key: 'id', name: 'ID', fieldName: 'id', minWidth: 200 },
                                    { key: 'tenantId', name: 'Tenant', fieldName: 'tenantId', minWidth: 120 },
                                    { key: 'mailboxId', name: 'Mailbox', fieldName: 'mailboxId', minWidth: 150 },
                                    { key: 'type', name: 'Type', fieldName: 'type', minWidth: 100 },
                                    { key: 'userIdInTenant', name: 'User In Tenant', fieldName: 'userIdInTenant', minWidth: 120 }
                                ]}
                            />
                        </PivotItem>

                        <PivotItem headerText="Sync Logs">
                            <DetailsList
                                items={selectedUser.syncLogs || []}
                                columns={[
                                    { key: 'timestamp', name: 'Time', onRender: (l) => formatDate(l.timestamp), minWidth: 150 },
                                    { key: 'status', name: 'Status', onRender: (l) => {
                                        const color = l.status === 'completed' ? 'green' : l.status === 'failed' ? 'red' : 'orange';
                                        return <span style={{ color }}>{l.status}</span>;
                                    }, minWidth: 100 },
                                    { key: 'eventsProcessed', name: 'Events', fieldName: 'eventsProcessed', minWidth: 80 },
                                    { key: 'direction', name: 'Direction', fieldName: 'direction', minWidth: 100 },
                                    { key: 'message', name: 'Message', fieldName: 'message', minWidth: 200 }
                                ]}
                            />
                        </PivotItem>

                        <PivotItem headerText="Sync Settings">
                            <Stack tokens={{ childrenGap: 10 }}>
                                {selectedUser.syncSettings?.length > 0 ? (
                                    selectedUser.syncSettings.map((s, i) => (
                                        <Stack key={i} styles={{ root: { border: '1px solid #eee', padding: 10 } }}>
                                            <Text>Refresh Interval: {s.refreshIntervalMinutes} minutes</Text>
                                            {s.upstreamTenantId && <Text>Upstream Tenant: {s.upstreamTenantId}</Text>}
                                            {s.downstreamTenantId && <Text>Downstream Tenant: {s.downstreamTenantId}</Text>}
                                            {s.lastSyncAt && <Text>Last Sync At: {formatDate(s.lastSyncAt)}</Text>}
                                        </Stack>
                                    ))
                                ) : (
                                    <Text>No sync settings configured.</Text>
                                )}
                            </Stack>
                        </PivotItem>
                    </Pivot>
                </Stack>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Delete User',
                    subText: userToDelete ? `Are you sure you want to delete user "${userToDelete.displayName || userToDelete.email || userToDelete.userId}" (${userToDelete.userId})? This will remove all accounts, tokens, calendars, sync settings, and logs.` : ''
                }}
                modal={true}
                hidden={!deleteDialogOpen}
                onDismiss={closeDeleteDialog}
            >
                <DialogFooter>
                    {deleteResult?.success ? (
                        <PrimaryButton onClick={closeDeleteDialog} text="Close" />
                    ) : deleteResult?.success === false ? (
                        <>
                            <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 10 } }}>
                                Delete failed: {deleteResult.error}
                            </MessageBar>
                            <PrimaryButton onClick={confirmDelete} text="Retry" />
                            <DefaultButton onClick={closeDeleteDialog} text="Cancel" />
                        </>
                    ) : (
                        <>
                            <PrimaryButton onClick={confirmDelete} text="Delete" />
                            <DefaultButton onClick={closeDeleteDialog} text="Cancel" />
                        </>
                    )}
                </DialogFooter>
            </Dialog>

            {/* Force Sync Dialog */}
            <Dialog
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Force Sync',
                    subText: userToSync ? `Trigger a calendar sync for user "${userToSync.displayName || userToSync.email || userToSync.userId}" (${userToSync.userId})?` : ''
                }}
                modal={true}
                hidden={!forceSyncDialogOpen}
                onDismiss={closeForceSyncDialog}
            >
                <DialogFooter>
                    {syncResult?.success ? (
                        <Stack tokens={{ childrenGap: 10 }}>
                            <MessageBar messageBarType={MessageBarType.success}>
                                Sync completed. {syncResult.result?.eventsProcessed || 0} events processed.
                            </MessageBar>
                            <PrimaryButton onClick={closeForceSyncDialog} text="Close" />
                        </Stack>
                    ) : syncResult?.success === false ? (
                        <>
                            <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 10 } }}>
                                Sync failed: {syncResult.error}
                            </MessageBar>
                            <PrimaryButton onClick={confirmForceSync} text="Retry" />
                            <DefaultButton onClick={closeForceSyncDialog} text="Cancel" />
                        </>
                    ) : syncing ? (
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <Spinner />
                            <Text>Syncing...</Text>
                        </Stack>
                    ) : (
                        <>
                            <PrimaryButton onClick={confirmForceSync} text="Sync" />
                            <DefaultButton onClick={closeForceSyncDialog} text="Cancel" />
                        </>
                    )}
                </DialogFooter>
            </Dialog>
        </Stack>
    );
}

export default AdminPanel;